// Toast notification display component — renders a stack of dismissible messages.
// Mounted by App.js via ToastStack; state flows through ToastContext.
//
// D-06: Toast is now the per-toast ROW (own styling, own auto-dismiss timer).
// ToastStack is the fixed-position container that lays multiple rows out in
// a vertical stack so simultaneous toasts (e.g. two background sync
// failures) both render instead of the second silently replacing the first.

import React from 'react';

// WR-01 (38-REVIEW.md): `id` and `onDismiss` (the stack's single stable
// dismissToast callback) are passed down separately rather than
// ToastStack pre-binding `() => onDismiss(toast.id)` at the call site.
// A closure allocated fresh on every ToastStack render changes identity on
// every render, so if it were this effect's dependency, React would tear
// down and re-arm EVERY live toast's timer whenever the stack re-rendered
// for an unrelated reason (another toast arriving or auto-dismissing) —
// exactly the burst-failure scenario D-06 exists to handle. Binding `id`
// here and calling `onDismiss(id)` internally means each toast's own timer
// depends only on its own `duration` and the one stack-stable `onDismiss`
// reference, so unrelated stack churn no longer resets it.
function Toast({ id, message, type, action, duration, onDismiss }) {
    // Phase 55: the timer pauses while the pointer is over the toast or focus
    // is inside it, and resumes with the time that was left. State lives in
    // refs so pausing never re-renders and never touches the effect's deps.
    const timerRef = React.useRef(null);
    const remainingRef = React.useRef(duration);
    const startedAtRef = React.useRef(0);
    const hoveredRef = React.useRef(false);
    const focusedRef = React.useRef(false);
    // The countdown bar's CSS animation runs for the same duration and is
    // paused and resumed with the timer, through the DOM, so it never drifts
    // from the time actually left and pausing still never re-renders.
    const barRef = React.useRef(null);
    const setBarRunning = (running) => {
        if (barRef.current) barRef.current.style.animationPlayState = running ? 'running' : 'paused';
    };

    React.useEffect(() => {
        // duration === null means "carries an action, persists until acted
        // on or dismissed" (D-05 Retry, D-08 Reopen) — skip the timer.
        if (duration === null) {
            return undefined;
        }
        remainingRef.current = duration ?? 3000;
        startedAtRef.current = Date.now();
        timerRef.current = setTimeout(() => {
            timerRef.current = null;
            onDismiss(id, 'timeout');
        }, remainingRef.current);
        return () => {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        };
    }, [id, duration, onDismiss]);

    const pause = () => {
        if (timerRef.current === null) return;
        clearTimeout(timerRef.current);
        timerRef.current = null;
        remainingRef.current = Math.max(0, remainingRef.current - (Date.now() - startedAtRef.current));
        setBarRunning(false);
    };

    const resume = () => {
        if (hoveredRef.current || focusedRef.current) return;
        if (duration === null || timerRef.current !== null) return;
        setBarRunning(true);
        startedAtRef.current = Date.now();
        timerRef.current = setTimeout(() => {
            timerRef.current = null;
            onDismiss(id, 'timeout');
        }, remainingRef.current);
    };

    const baseStyle = "relative overflow-hidden p-4 rounded-lg shadow-lg text-white transition-opacity duration-300";
    const typeStyle = (type === 'success' || type === 'info') ?"bg-[var(--color-primary)]" : "bg-[var(--color-danger)]";

    return (
        <div
            className={`${baseStyle} ${typeStyle}`}
            onMouseEnter={() => { hoveredRef.current = true; pause(); }}
            onMouseLeave={() => { hoveredRef.current = false; resume(); }}
            onFocus={() => { focusedRef.current = true; pause(); }}
            onBlur={(event) => {
                if (event.currentTarget.contains(event.relatedTarget)) return;
                focusedRef.current = false;
                resume();
            }}
        >
            <div className="flex items-center justify-between gap-3">
                <span>{message}</span>
                {action && (
                    <button
                        type="button"
                        onClick={() => {
                            action.onClick();
                            onDismiss(id, 'action');
                        }}
                        className="shrink-0 text-sm font-semibold underline underline-offset-2"
                    >
                        {action.label}
                    </button>
                )}
            </div>
            {/* How long until the toast goes away. A toast that waits for its
                action (duration null) has no countdown. */}
            {duration !== null && (
                <div
                    ref={barRef}
                    aria-hidden="true"
                    className="kf-toast-countdown absolute inset-x-0 bottom-0 h-1 bg-current opacity-50"
                    style={{ animationDuration: `${duration ?? 3000}ms`, animationPlayState: 'running' }}
                />
            )}
        </div>
    );
}

/**
 * ToastStack — the fixed-position container that stacks toasts vertically.
 *
 * Sits above the HelpFab (44px tall, pinned at bottom:20 right:20) rather
 * than under it. z-[10000] makes the stack the top layer of the scale: above
 * Modal and full-screen sheets (9990), the first-run and version overlays
 * (9995), and the nested pickers stacked over a Modal (9999). Feedback raised
 * from inside a dialog must never paint underneath it; at 9970 the Google
 * import sheet's opaque surface hid every toast it raised (Phase 63 review
 * WR-01). Toasts are transient and corner-anchored, so sitting above the
 * Help drawer (9985) costs nothing. Sized so all 5 visible toasts
 * (ToastContext's cap) remain on-screen and reachable rather than running
 * off the viewport.
 *
 * The live region is ALWAYS mounted, empty when there are no toasts. Screen
 * readers (VoiceOver, and NVDA with Chrome) announce only changes inside a
 * live region that already existed, so mounting it together with the first
 * toast would leave that toast (usually the only one) unannounced.
 */
export function ToastStack({ toasts = [], onDismiss }) {
    const list = toasts || [];
    return (
        <div role="status" aria-live="polite" className="fixed bottom-24 right-5 z-[10000] flex flex-col gap-2 max-h-[80vh] overflow-y-auto">
            {list.map((toast) => (
                <Toast
                    key={toast.id}
                    id={toast.id}
                    message={toast.message}
                    type={toast.type}
                    action={toast.action}
                    duration={toast.duration}
                    onDismiss={onDismiss}
                />
            ))}
        </div>
    );
}

export default Toast;
